import React, { useState } from 'react'
import { Box, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import CheckboxField from '../../../../../../../../component/CheckboxField';
import BtnPrimary from "../../../../../../../../component/PrimaryButton";
import FormField from '../../../../../../../../component/Formfield';
import { FormSection, FieldLabel, SwitchRow, WizardActions } from '../../../../../../../../component/wizard';
import { tokens } from '../../../../../../../../theme';

const AbsenteeismForm = ({ loadNextPage, absenteeismForm, setAbsenteeismForm }) => {

  const [isWorkMinToPresent, setIsWorkMinToPresent] = useState(false);

  const handleChange = (field, value) => {
    setAbsenteeismForm(prevState => ({ ...prevState, [field]: value }));
  };

  const isPenalised = absenteeismForm?.absenteeismPenalty !== "none";
  const labelMuted = { fontSize: "0.875rem", lineHeight: "1.4", color: tokens.textMuted };

  return (
    <Box>
      <FormSection title="Absenteeism" description="Penalize employees who exceed the allowed number of uninformed absences.">
        <SwitchRow
          title="Enable absenteeism tracking"
          description="Flag and optionally penalise uninformed absences."
          checked={absenteeismForm?.enableAbsenteeism}
          onChange={(e) => handleChange('enableAbsenteeism', e.target.checked)}
        />
        <Box sx={{ mt: "1.25rem" }}>
          <FieldLabel>Auto-mark absent when there's no clock-in?</FieldLabel>
          <RadioGroup
            value={String(absenteeismForm?.autoDetectAbsenteeism)}
            onChange={(e) => handleChange('autoDetectAbsenteeism', e.target.value === "true")}
            sx={{ flexDirection: "row", gap: "1.5rem" }}
          >
            <FormControlLabel value="false" control={<Radio />} label={
              <Box>No<Box sx={labelMuted}>Don't auto-detect; mark absences manually.</Box></Box>
            } />
            <FormControlLabel value="true" control={<Radio />} label={
              <Box>Yes<Box sx={labelMuted}>Detect absences automatically using the rules below.</Box></Box>
            } />
          </RadioGroup>
        </Box>
      </FormSection>

      <FormSection title="Presence rules" description="What counts as present, and the warning threshold before penalties apply.">
        <CheckboxField
          label="Require a minimum number of working hours to be considered present for the day."
          checked={isWorkMinToPresent}
          onChange={(isChecked) => setIsWorkMinToPresent(isChecked)}
          labelStyle={{ fontSize: "0.875rem", lineHeight: "1.4", color: tokens.text }}
        />
        {isWorkMinToPresent && (
          <Box sx={{ mt: "1rem" }}>
            <FieldLabel>Minimum hours to be marked present</FieldLabel>
            <FormField type="text" width="7rem" height="2.75rem" noMargin
              value={absenteeismForm.minDailyHours}
              onChange={(e) => handleChange('minDailyHours', e.target.value)} />
          </Box>
        )}
        <Box sx={{ mt: "1.25rem" }}>
          <FieldLabel>Unreported absences before a warning</FieldLabel>
          <Box sx={{ display: "flex", alignItems: "center", gap: "0.75rem", color: tokens.textMuted, fontSize: "0.875rem" }}>
            <FormField type="text" width="6rem" height="2.75rem" noMargin
              value={absenteeismForm.absenteeismThreshold}
              onChange={(e) => handleChange('absenteeismThreshold', e.target.value)} />
            day(s) in a
            <FormField type="select" width="10rem" height="2.75rem" noMargin
              options={[{ name: "Week", value: "weekly" }, { name: "Month", value: "monthly" }, { name: "Year", value: "yearly" }]}
              value={absenteeismForm.absenteeismTimePeriod}
              onChange={(e) => handleChange('absenteeismTimePeriod', e.target.value)} />
          </Box>
        </Box>
      </FormSection>

      <FormSection title="Adjoining days" description="Optionally penalise absences that bracket a holiday or weekend.">
        <CheckboxField
          label={
            <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
              Penalise an adjoining holiday when there are
              <FormField type="text" width="5rem" height="2.25rem" noMargin
                value={absenteeismForm.minAbsenceDurationForAdjoiningHolidayPenalty}
                onChange={(e) => handleChange('minAbsenceDurationForAdjoiningHolidayPenalty', e.target.value)} />
              or more day(s) with no attendance.
            </Box>
          }
          checked={absenteeismForm.penaliseAdjoiningHolidays}
          onChange={(isChecked) => handleChange('penaliseAdjoiningHolidays', isChecked)}
          labelStyle={{ fontSize: "0.875rem", color: tokens.text }}
        />
        <Box sx={{ mt: "0.75rem" }}>
          <CheckboxField
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                Penalise an adjoining weekend off when there are
                <FormField type="text" width="5rem" height="2.25rem" noMargin
                  value={absenteeismForm.minAbsenceDurationForAdjoiningWeekendPenalty}
                  onChange={(e) => handleChange('minAbsenceDurationForAdjoiningWeekendPenalty', e.target.value)} />
                or more day(s) with no attendance.
              </Box>
            }
            checked={absenteeismForm.penaliseAdjoiningWeekends}
            onChange={(isChecked) => handleChange('penaliseAdjoiningWeekends', isChecked)}
            labelStyle={{ fontSize: "0.875rem", color: tokens.text }}
          />
        </Box>
      </FormSection>

      <FormSection title="Penalty on breach" description="What happens when an employee exceeds the allowed unreported absences.">
        <SwitchRow
          title="Apply a penalty"
          description="Off = absences are tracked but not penalised."
          checked={isPenalised}
          onChange={(e) => handleChange('absenteeismPenalty', e.target.checked ? "deduct" : "none")}
        />
        {isPenalised && (
          <Box sx={{ mt: "1.25rem" }}>
            <FieldLabel>How are penalties charged?</FieldLabel>
            <RadioGroup
              value={absenteeismForm?.absenteeismPenalty}
              onChange={(e) => handleChange('absenteeismPenalty', e.target.value)}
              sx={{ flexDirection: "row", gap: "1.5rem", mb: "1.25rem" }}
            >
              <FormControlLabel value="deduct" control={<Radio />} label={
                <Box>Loss of Pay<Box sx={labelMuted}>Dock pay for each incident.</Box></Box>
              } />
              <FormControlLabel value="leave" control={<Radio />} label={
                <Box>Paid Leave<Box sx={labelMuted}>Deduct from the employee's paid-leave balance.</Box></Box>
              } />
            </RadioGroup>
            <Box sx={{ fontSize: "0.875rem", color: tokens.text, mb: "0.5rem" }}>
              After the warning threshold, how is the employee penalised?
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem", color: tokens.textMuted, fontSize: "0.875rem" }}>
              {absenteeismForm?.absenteeismPenalty === "deduct" ? (
                <>
                  <FormField type="text" width="6rem" height="2.5rem" noMargin
                    value={absenteeismForm?.noOfDaysOfPayLossForAbsenteeism}
                    onChange={(e) => handleChange('noOfDaysOfPayLossForAbsenteeism', e.target.value)} />
                  day(s) loss of pay per incident.
                </>
              ) : (
                <>
                  <FormField type="text" width="6rem" height="2.5rem" noMargin
                    value={absenteeismForm?.noOfPaidLeaveDeductForAbsenteeism}
                    onChange={(e) => handleChange('noOfPaidLeaveDeductForAbsenteeism', e.target.value)} />
                  day(s) of paid leave per incident.
                </>
              )}
            </Box>
          </Box>
        )}
      </FormSection>

      <WizardActions>
        <BtnPrimary sx={{ borderRadius: "8px" }} onClick={() => loadNextPage("chooseCategory")}>
          Save
        </BtnPrimary>
      </WizardActions>
    </Box>
  )
}

export default AbsenteeismForm
